// pipe 4B.8b (D6): the refresh token at rest lives ONLY in the hardware Keystore behind a
// biometric gate — NEVER in WebView storage (Secure-by-default). Web is a no-op: the SPA keeps
// its in-memory + sessionStorage ACCESS-token path (no long-lived secret on web).
import { isNative } from './platform';
const SERVER = 'pipe-driver';
// NON-SECRET marker (not the token) recording that a refresh token is stored in the Keystore. Lets
// the boot skip the biometric prompt entirely when there is nothing to unlock (first launch / after
// logout/clear) → straight to PKCE login, instead of prompting biometric for a credential that
// cannot exist (which on a device with no enrolled biometric can block the boot). The refresh token
// VALUE never touches WebView storage — only this boolean does (Secure-by-default preserved).
const RT_MARKER = 'pipe.driver.has_rt';
function setMarker(): void {
  try { localStorage.setItem(RT_MARKER, '1'); } catch { /* ignore */ }
}
function hasMarker(): boolean {
  try { return localStorage.getItem(RT_MARKER) === '1'; } catch { return false; }
}
function clearMarker(): void {
  try { localStorage.removeItem(RT_MARKER); } catch { /* ignore */ }
}
export async function persistRefreshToken(rt: string): Promise<void> {
  if (!isNative()) return;
  // Best-effort: a Keystore/biometric failure (e.g. no device credential enrolled) must NEVER break
  // the login/refresh flow — the user is already authed via the access token; they just won't get
  // biometric-resume next launch. Only mark when the credential actually persisted.
  try {
    const { NativeBiometric } = await import('capacitor-native-biometric');
    await NativeBiometric.setCredentials({ username: 'driver', password: rt, server: SERVER });
    setMarker();
  } catch {
    /* biometric/Keystore unavailable — skip persistence; login proceeds (no resume) */
  }
}
export async function loadRefreshToken(): Promise<string | null> {
  if (!isNative()) return null;
  // No stored token (no marker) → never prompt biometric; the caller falls back to login().
  if (!hasMarker()) return null;
  try {
    const { NativeBiometric } = await import('capacitor-native-biometric');
    const avail = await NativeBiometric.isAvailable();
    if (!avail.isAvailable) return null;
    await NativeBiometric.verifyIdentity({ reason: 'Unlock PIPE Driver', title: 'PIPE Driver' });
    const c = await NativeBiometric.getCredentials({ server: SERVER });
    return c.password ?? null;
  } catch { return null; }
}
export async function clearRefreshToken(): Promise<void> {
  if (!isNative()) return;
  clearMarker();
  try {
    const { NativeBiometric } = await import('capacitor-native-biometric');
    await NativeBiometric.deleteCredentials({ server: SERVER });
  } catch { /* already absent */ }
}
